import { supabase } from '../lib/supabase';

export const seoService = {
  // ============ KEYWORDS ============
  async getAllKeywords() {
    const { data, error } = await supabase?.from('seo_keywords')?.select('*')?.order('search_volume', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createKeyword(keywordData) {
    const { data, error } = await supabase?.from('seo_keywords')?.insert([{
        keyword: keywordData?.keyword,
        search_volume: keywordData?.searchVolume || 0,
        difficulty: keywordData?.difficulty || 'medium',
        current_ranking: keywordData?.currentRanking || null,
        target_ranking: keywordData?.targetRanking || null,
        target_url: keywordData?.targetUrl || null,
        category: keywordData?.category || 'general',
        notes: keywordData?.notes || null
      }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  async updateKeyword(keywordId, updates) {
    const { data, error } = await supabase?.from('seo_keywords')?.update({
        keyword: updates?.keyword,
        search_volume: updates?.searchVolume,
        difficulty: updates?.difficulty,
        current_ranking: updates?.currentRanking,
        target_ranking: updates?.targetRanking,
        target_url: updates?.targetUrl,
        category: updates?.category,
        notes: updates?.notes,
        updated_at: new Date()?.toISOString()
      })?.eq('id', keywordId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async deleteKeyword(keywordId) {
    const { error } = await supabase?.from('seo_keywords')?.delete()?.eq('id', keywordId);

    if (error) throw error;
  },

  // ============ PAGE OPTIMIZATIONS ============
  async getAllPageOptimizations() {
    const { data, error } = await supabase?.from('seo_page_optimizations')?.select('*')?.order('seo_score', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createPageOptimization(pageData) {
    const { data, error } = await supabase?.from('seo_page_optimizations')?.insert([{
        page_url: pageData?.pageUrl,
        page_title: pageData?.pageTitle,
        meta_description: pageData?.metaDescription || null,
        h1_tag: pageData?.h1Tag || null,
        target_keywords: pageData?.targetKeywords || [],
        seo_score: pageData?.seoScore || 0,
        recommendations: pageData?.recommendations || []
      }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  async updatePageOptimization(pageId, updates) {
    const { data, error } = await supabase?.from('seo_page_optimizations')?.update({
        page_url: updates?.pageUrl,
        page_title: updates?.pageTitle,
        meta_description: updates?.metaDescription,
        h1_tag: updates?.h1Tag,
        target_keywords: updates?.targetKeywords,
        seo_score: updates?.seoScore,
        recommendations: updates?.recommendations,
        updated_at: new Date()?.toISOString()
      })?.eq('id', pageId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async deletePageOptimization(pageId) {
    const { error } = await supabase?.from('seo_page_optimizations')?.delete()?.eq('id', pageId);

    if (error) throw error;
  },

  // ============ ANALYTICS ============
  async getAnalyticsMetrics(startDate, endDate) {
    let query = supabase?.from('seo_analytics')?.select('*')?.order('date', { ascending: false });

    if (startDate) {
      query = query?.gte('date', startDate);
    }
    if (endDate) {
      query = query?.lte('date', endDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async createAnalyticsEntry(analyticsData) {
    const { data, error } = await supabase?.from('seo_analytics')?.insert([{
        date: analyticsData?.date,
        organic_traffic: analyticsData?.organicTraffic || 0,
        impressions: analyticsData?.impressions || 0,
        clicks: analyticsData?.clicks || 0,
        ctr: analyticsData?.ctr || 0,
        average_position: analyticsData?.averagePosition || 0,
        bounce_rate: analyticsData?.bounceRate || 0,
        conversion_rate: analyticsData?.conversionRate || 0
      }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  // ============ SITEMAP ============
  async getSitemapConfig() {
    const { data, error } = await supabase?.from('seo_sitemap_config')?.select('*')?.order('created_at', { ascending: false })?.limit(1)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return data || null;
  },

  async updateSitemapConfig(configData) {
    const existing = await this.getSitemapConfig();

    if (existing) {
      const { data, error } = await supabase?.from('seo_sitemap_config')?.update({
          sitemap_url: configData?.sitemapUrl,
          last_generated: configData?.lastGenerated || new Date()?.toISOString(),
          last_submitted: configData?.lastSubmitted || null,
          indexing_status: configData?.indexingStatus || 'pending',
          pages_indexed: configData?.pagesIndexed || 0,
          total_pages: configData?.totalPages || 0,
          updated_at: new Date()?.toISOString()
        })?.eq('id', existing?.id)?.select()?.single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase?.from('seo_sitemap_config')?.insert([{
          sitemap_url: configData?.sitemapUrl,
          last_generated: configData?.lastGenerated || new Date()?.toISOString(),
          last_submitted: configData?.lastSubmitted || null,
          indexing_status: configData?.indexingStatus || 'pending',
          pages_indexed: configData?.pagesIndexed || 0,
          total_pages: configData?.totalPages || 0
        }])?.select()?.single();

      if (error) throw error;
      return data;
    }
  },

  // ============ PERFORMANCE METRICS ============
  async getPerformanceMetrics(startDate, endDate) {
    let query = supabase?.from('seo_performance_metrics')?.select('*')?.order('date', { ascending: false });

    if (startDate) {
      query = query?.gte('date', startDate);
    }
    if (endDate) {
      query = query?.lte('date', endDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async createPerformanceMetric(metricData) {
    const { data, error } = await supabase?.from('seo_performance_metrics')?.insert([{
        date: metricData?.date,
        page_url: metricData?.pageUrl,
        page_load_speed: metricData?.pageLoadSpeed || 0,
        mobile_score: metricData?.mobileScore || 0,
        desktop_score: metricData?.desktopScore || 0,
        core_web_vitals_lcp: metricData?.coreWebVitalsLcp || 0,
        core_web_vitals_fid: metricData?.coreWebVitalsFid || 0,
        core_web_vitals_cls: metricData?.coreWebVitalsCls || 0
      }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  // ============ DASHBOARD METRICS ============
  async getDashboardMetrics() {
    try {
      const [keywords, pages, analytics, sitemap] = await Promise.all([
        this.getAllKeywords(),
        this.getAllPageOptimizations(),
        this.getAnalyticsMetrics(),
        this.getSitemapConfig()
      ]);

      // Calculate aggregated metrics
      const totalKeywords = keywords?.length || 0;
      const avgRanking = keywords?.length > 0
        ? keywords?.reduce((sum, k) => sum + (k?.current_ranking || 0), 0) / keywords?.length
        : 0;

      const totalPages = pages?.length || 0;
      const avgSeoScore = pages?.length > 0
        ? pages?.reduce((sum, p) => sum + (p?.seo_score || 0), 0) / pages?.length
        : 0;

      const latestAnalytics = analytics?.[0] || {};

      return {
        keywords: {
          total: totalKeywords,
          avgRanking: avgRanking?.toFixed(1),
          topRanked: keywords?.filter(k => k?.current_ranking <= 10)?.length || 0
        },
        pages: {
          total: totalPages,
          avgScore: avgSeoScore?.toFixed(0),
          needsOptimization: pages?.filter(p => p?.seo_score < 70)?.length || 0
        },
        analytics: {
          organicTraffic: latestAnalytics?.organic_traffic || 0,
          impressions: latestAnalytics?.impressions || 0,
          clicks: latestAnalytics?.clicks || 0,
          ctr: latestAnalytics?.ctr || 0,
          avgPosition: latestAnalytics?.average_position || 0
        },
        sitemap: sitemap || {
          sitemap_url: null,
          indexing_status: 'not_configured',
          pages_indexed: 0,
          total_pages: 0
        },
        keywordsList: keywords?.slice(0, 10) || [],
        pagesList: pages?.slice(0, 10) || [],
        analyticsHistory: analytics?.slice(0, 30) || []
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }
};

export default seoService;